/**
 * Tutorial System Type Definitions
 *
 * Defines interfaces for the interactive tutorial overlay system
 * that guides new users through the lobby and game interfaces.
 */

/**
 * Target element specification for highlighting
 */
export interface TutorialTarget {
  /** CSS selector for the target element */
  selector: string
  /** Fallback selector if primary not found */
  fallbackSelector?: string
  /** Data attribute to find element (e.g., 'draw-pile' for data-tutorial="draw-pile") */
  dataAttribute?: string
}

/**
 * Tooltip positioning relative to highlighted element
 */
export type TooltipPosition =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center' // For modal-like display without element targeting

/**
 * Individual tutorial step definition
 */
export interface TutorialStep {
  /** Unique step identifier */
  id: string
  /** Target element to highlight (null for intro/outro steps) */
  target: TutorialTarget | null
  /** Step title */
  title: string
  /** Step description (supports basic formatting: **bold**, *italic*) */
  description: string
  /** Optional screenshot/image path */
  image?: string
  /** Tooltip position relative to target */
  position: TooltipPosition
  /** Whether to allow clicking the highlighted element */
  allowInteraction?: boolean
  /** Optional action hint (e.g., "Click to try!") */
  actionHint?: string
  /** Material Symbols icon name */
  icon?: string
  /** Padding around highlighted element (px) */
  spotlightPadding?: number
  /** If true, user must perform the action (no Skip/Next buttons shown) */
  requiresAction?: boolean
}

/**
 * Tutorial context identifiers
 */
export type TutorialId = 'lobby' | 'create-game' | 'waiting-room' | 'game'

/**
 * Tutorial sequence definition
 */
export interface Tutorial {
  /** Unique tutorial identifier */
  id: TutorialId
  /** Display name */
  name: string
  /** Tutorial description */
  description: string
  /** Ordered array of steps */
  steps: TutorialStep[]
  /** Total estimated duration (seconds) */
  estimatedDuration: number
  /** Next tutorial to auto-start after this one completes (for chained tutorials) */
  nextTutorial?: TutorialId
}

/**
 * Tutorial state for persistence
 */
export interface TutorialState {
  /** Which tutorials have been completed (as array for JSON serialization) */
  completedTutorials: string[]
  /** Has user seen first-visit prompt */
  hasSeenFirstVisitPrompt: boolean
  /** Current active tutorial (if any) */
  activeTutorial: TutorialId | null
  /** Current step index within active tutorial */
  currentStepIndex: number
  /** User preference: never auto-prompt again */
  disableAutoPrompt: boolean
  /** Whether we're in a guided first-time flow (auto-advance through screens) */
  isFirstTimeFlow: boolean
}

/**
 * Calculated position for spotlight cutout
 */
export interface SpotlightRect {
  x: number
  y: number
  width: number
  height: number
  borderRadius: number
}

/**
 * Calculated position for tooltip
 */
export interface TooltipStyles {
  top?: string
  bottom?: string
  left?: string
  right?: string
  transform?: string
}

/**
 * localStorage key for tutorial state
 */
export const TUTORIAL_STORAGE_KEY = 'canasta_tutorial_state'

/**
 * Default state for first-time users
 */
export const DEFAULT_TUTORIAL_STATE: TutorialState = {
  completedTutorials: [],
  hasSeenFirstVisitPrompt: false,
  activeTutorial: null,
  currentStepIndex: 0,
  disableAutoPrompt: false,
  isFirstTimeFlow: false
}
